import { Button, Modal, ModalBody, ModalContent, ModalHeader, NavbarItem, useDisclosure } from "@heroui/react";
import { WalletIcon } from "lucide-react";
import { type PropsWithChildren, type ReactNode, useEffect, useState } from "react";
import { toast } from "sonner";
import { type Connector, useAccount, useConnect } from "wagmi";
import TokenBrandedCoinbase from "~icons/token-branded/coinbase";
import TokenBrandedMetamask from "~icons/token-branded/metamask";
import AztecSymbol from "../assets/aztec-symbol.svg";
import DividerDarkVertical from "../assets/divider-dark-vertical.svg";
import { useAztecWallet } from "../hooks/useAztecWallet";
import { shortHex } from "../utils";
import { CheckCircle, NumberRing } from "./Step";

export function ConnectWallets({ onChange }: { onChange: (open: boolean) => void }) {
  const { isOpen, onOpen, onOpenChange } = useDisclosure({
    onChange,
  });

  const { address } = useAccount();
  const { address: aztecAddress, isConnected: isAztecConnected } = useAztecWallet();

  // Set selected step based on the user's wallet connection status
  const [selectedStep, setSelectedStep] = useState<"connect-evm-wallet" | "connect-aztec-wallet" | "done">(
    "connect-evm-wallet",
  );

  useEffect(() => {
    if (address && !isAztecConnected) {
      setSelectedStep("connect-aztec-wallet");
    } else if (address && isAztecConnected) {
      setSelectedStep("done");
      // Close modal after both wallets are connected
      onChange(false);
    } else if (!address) {
      setSelectedStep("connect-evm-wallet");
    }
  }, [address, isAztecConnected, onChange]);

  const evmWalletState = address ? "connected" : selectedStep === "connect-evm-wallet" ? "selected" : "idle";
  const aztecWalletState = isAztecConnected
    ? "connected"
    : selectedStep === "connect-aztec-wallet"
      ? "selected"
      : "idle";

  return (
    <NavbarItem>
      <Button
        color="primary"
        radius="full"
        size="lg"
        className="font-semibold"
        id="connect-wallet-btn"
        onPress={onOpen}
      >
        Connect Wallets
      </Button>
      <Modal
        isOpen={isOpen}
        onOpenChange={onOpenChange}
        backdrop="blur"
        size="2xl"
        classNames={{ base: "turnstile-modal-border" }}
      >
        <ModalContent>
          <ModalHeader className="flex flex-col">
            <p className="font-semibold text-large text-foreground">Connect wallets</p>
            <p className="font-light text-small w-2/3">
              To properly use all the functionality of the application, connect your EVM and Aztec wallet.
            </p>
          </ModalHeader>

          <ModalBody className="flex flex-row gap-4 items-stretch py-4">
            <div>
              {/* <EthWallet /> */}
              <ConnectWalletButton step={1} state={evmWalletState}>
                {evmWalletState === "connected" ? (
                  <div className="inline-flex flex-col gap-1">
                    <div className="text-[#C2C9CF] text-small">Connected EVM Wallet</div>
                    <div className="text-white text-small font-semibold inline-flex items-center gap-2">
                      <TokenBrandedMetamask className="size-6" />
                      <span>{shortHex(address ?? "0x")}</span>
                    </div>
                  </div>
                ) : (
                  "Connect EVM Wallet"
                )}
              </ConnectWalletButton>

              <div className="mx-8 my-2 h-12 border-l-2 border-dashed" />

              <ConnectWalletButton step={2} state={aztecWalletState}>
                {aztecWalletState === "connected" ? (
                  <div className="inline-flex flex-col gap-1">
                    <div className="text-[#C2C9CF] text-small">Connected Aztec Wallet</div>
                    <div className="text-white text-small font-semibold inline-flex items-center gap-2">
                      <img src={AztecSymbol} alt="Aztec symbol" className="size-6" />
                      <span>{shortHex(aztecAddress?.toString() ?? "0x")}</span>
                    </div>
                  </div>
                ) : (
                  "Connect Aztec Wallet"
                )}
              </ConnectWalletButton>

              <p className="font-light text-small mt-4">
                By connecting your wallet you agree with our <a href="#terms-of-service">Terms of Service</a> and{" "}
                <a href="#privacy-policy">Privacy Policy</a>.
              </p>
            </div>

            <img src={DividerDarkVertical} alt="divider dark vertical" className="h-full" />

            {selectedStep === "connect-evm-wallet" ? <EvmWalletList /> : <AztecWalletList />}
          </ModalBody>
        </ModalContent>
      </Modal>
    </NavbarItem>
  );
}

function ConnectWalletButton({
  state,
  step,
  onPress,
  children,
}: PropsWithChildren<{
  state: "idle" | "selected" | "connected";
  step: number;
  onPress?: () => void;
}>) {
  switch (state) {
    case "idle":
      return (
        <Button
          size="lg"
          radius="sm"
          startContent={<NumberRing digit={step} isSelected={false} />}
          style={{
            backgroundColor: "#3D637F",
            color: "#C2C9CF",
          }}
          className="px-4 py-8 pr-6"
          onPress={onPress}
        >
          {children}
        </Button>
      );
    case "selected":
      return (
        <Button
          size="lg"
          radius="sm"
          startContent={<NumberRing digit={step} isSelected={true} />}
          style={{
            backgroundColor: "#497393",
            color: "#FFFFFF",
          }}
          className="px-4 py-8 pr-6"
          onPress={onPress}
        >
          {children}
        </Button>
      );
    case "connected":
      return (
        <Button
          size="lg"
          radius="sm"
          startContent={<CheckCircle />}
          style={{
            backgroundColor: "#3D637F",
            color: "#C2C9CF",
          }}
          className="px-4 py-8 pr-6"
          onPress={onPress}
        >
          {children}
        </Button>
      );
  }
}

function ConnectorButton({
  state,
  name,
  icon,
  onPress,
}: {
  state: "idle" | "idle-installed" | "connecting" | "connected";
  name: string;
  icon: ReactNode;
  onPress: () => void;
}) {
  switch (state) {
    case "idle":
      return (
        <Button
          fullWidth
          size="lg"
          radius="sm"
          startContent={icon}
          onPress={onPress}
          className="bg-[#497393] !px-4 !py-8"
        >
          <div className="flex items-center justify-between flex-1">{name}</div>
        </Button>
      );
    case "idle-installed":
      return (
        <Button
          fullWidth
          size="lg"
          radius="sm"
          startContent={icon}
          onPress={onPress}
          className="bg-[#497393] !px-4 !py-8"
        >
          <div className="flex items-center justify-between flex-1">
            {name}
            <span className="text-[#ADC0D0] font-light text-small">Installed</span>
          </div>
        </Button>
      );
    case "connecting":
      return (
        <Button
          fullWidth
          size="lg"
          radius="sm"
          startContent={icon}
          onPress={onPress}
          data-active="true"
          className="bg-[#497393] !px-4 !py-8 data-[active=true]:ring-[#14DBC0] data-[active=true]:ring-2"
        >
          <div className="flex items-center justify-between flex-1">
            {name}
            <span className="text-sm text-[#14DBC0]">Connecting...</span>
          </div>
        </Button>
      );
    case "connected":
      return (
        <Button
          fullWidth
          size="lg"
          radius="sm"
          startContent={icon}
          onPress={onPress}
          className="bg-[#497393] !px-4 !py-8"
        >
          <div className="flex items-center justify-between flex-1">
            {name}
            <span className="inline-flex gap-1 items-center">
              <span className="size-1.5 rounded-full bg-[#14DBC0]" />
              <span className="text-[#14DBC0] text-small">Connected</span>
            </span>
          </div>
        </Button>
      );
    default:
      return null;
  }
}

function EvmConnectorButton({
  isSelected,
  connector,
  isPending,
  onConnect,
}: {
  isSelected: boolean;
  connector: Connector;
  isPending: boolean;
  onConnect: (connector: Connector) => Promise<void>;
}) {
  const { connector: activeConnector } = useAccount();

  const isConnecting = isSelected && isPending;
  const isConnected = activeConnector ? activeConnector.uid === connector.uid : false;

  const state = isConnected
    ? "connected"
    : isConnecting
      ? "connecting"
      : connector.type === "injected"
        ? "idle-installed"
        : "idle";

  return (
    <ConnectorButton
      state={state}
      name={connector.name}
      icon={
        connector.type === "metaMask" ? (
          <TokenBrandedMetamask className="size-8" />
        ) : connector.type === "coinbaseWallet" ? (
          <TokenBrandedCoinbase className="size-8" />
        ) : connector.icon ? (
          <img src={connector.icon} alt="wallet icon" />
        ) : (
          <WalletIcon />
        )
      }
      onPress={() => onConnect(connector)}
    />
  );
}

export function EvmWalletList() {
  const { connectAsync, connectors, isPending } = useConnect();
  const [selectedConnector, setSelectedConnector] = useState("");

  async function onConnect(connector: Connector) {
    setSelectedConnector(connector.uid);
    await connectAsync({ connector });
    setSelectedConnector("");
  }

  return (
    <div className="flex flex-col gap-2 h-full w-full content-start">
      {connectors.map((connector) => (
        <EvmConnectorButton
          key={connector.uid}
          isSelected={selectedConnector === connector.uid}
          isPending={isPending}
          connector={connector}
          onConnect={onConnect}
        />
      ))}
    </div>
  );
}

function AztecWalletList() {
  const { connect, isConnecting, isConnected } = useAztecWallet();

  const handleDemoWalletConnect = async () => {
    try {
      console.debug("=== STARTING AZTEC WALLET CONNECTION ===");
      console.debug("🚀 Pre-connection state:", { isConnecting, isConnected });

      await connect();

      // Add delay to check final state after connection attempt
      setTimeout(() => {
        console.debug("⏰ Post-connection state (after 1s delay):", {
          isConnecting,
          isConnected,
          timestamp: new Date().toISOString(),
        });
      }, 1000);

      // Add another check after 3 seconds to see if state eventually syncs
      setTimeout(() => {
        console.debug("⏰ Post-connection state (after 3s delay):", {
          isConnecting,
          isConnected,
          timestamp: new Date().toISOString(),
        });
      }, 3000);

      toast.success("Aztec wallet connected successfully");
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : "Failed to connect Aztec wallet";
      console.error("❌ Aztec wallet connection failed:", error);
      toast.error(errorMessage);
    }
  };

  return (
    <div className="flex flex-col gap-2 h-full w-full content-start">
      <ConnectorButton
        state={isConnected ? "connected" : isConnecting ? "connecting" : "idle-installed"}
        name="Demo Wallet"
        icon={<img src={AztecSymbol} alt="Aztec symbol" className="size-6" />}
        onPress={handleDemoWalletConnect}
      />
      <ConnectorButton
        state="idle"
        name="Other Aztec wallet"
        icon={<img src={AztecSymbol} alt="Aztec symbol" className="size-6" />}
        onPress={() => {}}
      />
    </div>
  );
}
